import clsx from "clsx";
import React from "react";
import {
  DialogProps,
  TextField,
  TextFieldProps,
  Select,
  MenuItem,
  SelectProps,
  ListItem,
  ListItemText,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ProgramTemplateCategory } from "@growth-machine-llc/stridist-api-client";

import { BaseDialog } from "./BaseDialog";
import { FieldsGroup } from "../fields/FieldsGroup";
import { FieldError } from "../fields/FieldError";
import LoadingButton from "../button/LoadingButton";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    [theme.breakpoints.up("md")]: {
      minWidth: theme.spacing(84),
    },
  },

  control: {
    marginBottom: theme.spacing(4),
    width: "100%",
  },

  fieldGroupLabel: {
    marginBottom: theme.spacing(1),
  },

  error: {
    position: "absolute",
  },

  button: {
    height: theme.spacing(7),
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    marginTop: theme.spacing(2),
  },

  primaryText: {
    fontSize: 16,
    fontWeight: 500,
  },

  selectPlaceholder: {
    color: theme.palette.text.secondary,
  },

  select: {
    "& > div": {
      padding: 0,
    },
  },
}));

const getCategoryLabel = (category: ProgramTemplateCategory): string => {
  if (category === ProgramTemplateCategory.NONE) return "None";
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

export interface CreateTemplateFromProgramDialogProps extends DialogProps {
  programName?: string;
  onConfirm: (name: string, category: ProgramTemplateCategory) => void;
  isPending?: boolean;
}

export function CreateTemplateFromProgramDialog(
  props: CreateTemplateFromProgramDialogProps,
) {
  const {
    className,
    onClose,
    programName = "",
    onConfirm,
    isPending = false,
    open,
    ...other
  } = props;
  const s = useStyles();
  const [name, setName] = React.useState(programName);
  const [category, setCategory] = React.useState<ProgramTemplateCategory>(
    ProgramTemplateCategory.NONE,
  );
  const [error, setError] = React.useState("");

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setName(programName || "");
      setCategory(ProgramTemplateCategory.NONE);
      setError("");
    }
  }, [open, programName]);

  const handleNameChange: TextFieldProps["onChange"] = React.useCallback(
    (event) => {
      const value = event.target.value;
      setName(value);

      if (error && value.trim().length >= 4) {
        setError("");
      }
    },
    [error],
  );

  const handleCategoryChange: SelectProps["onChange"] = React.useCallback(
    (event) => {
      setCategory(event.target.value as ProgramTemplateCategory);
    },
    [],
  );

  const resetState = React.useCallback(() => {
    setName(programName);
    setCategory(ProgramTemplateCategory.NONE);
    setError("");
  }, [programName]);

  const handleClose = React.useCallback(() => {
    resetState();
    if (onClose) {
      onClose({}, "backdropClick");
    }
  }, [onClose, resetState]);

  const handleConfirm = React.useCallback(() => {
    if (name.trim().length < 4) {
      setError("Name must be at least 4 characters long");
      return;
    }

    if (!name.trim()) {
      setError("Name cannot be empty");
      return;
    }

    setError("");
    onConfirm(name.trim(), category);
  }, [name, category, onConfirm]);

  const disabled = isPending;

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title="Create template from program"
      closeDisabled={disabled}
      onClose={handleClose}
      PaperProps={{ className: s.paper }}
      open={open}
      {...other}
    >
      <FieldsGroup
        className={clsx(s.control)}
        labelClassName={s.fieldGroupLabel}
        label="Template name"
        {...other}
      >
        <TextField
          variant="outlined"
          fullWidth
          id="name"
          placeholder="Enter template name..."
          value={name}
          onChange={handleNameChange}
          disabled={disabled}
          required
          autoFocus
        />
        {error && (
          <FieldError hideIcon={true} className={s.error}>
            {error}
          </FieldError>
        )}
      </FieldsGroup>

      <FieldsGroup
        className={clsx(s.control)}
        labelClassName={s.fieldGroupLabel}
        label="Category"
        {...other}
      >
        <Select
          variant="outlined"
          fullWidth
          id="category"
          className={s.select}
          value={category}
          onChange={handleCategoryChange}
          disabled={disabled}
          displayEmpty
          renderValue={(value) => {
            const selectedCategory = value as ProgramTemplateCategory;
            if (selectedCategory === ProgramTemplateCategory.NONE) {
              return (
                <ListItem component="div">
                  <ListItemText
                    classes={{
                      primary: clsx(s.primaryText, s.selectPlaceholder),
                    }}
                  >
                    Select category...
                  </ListItemText>
                </ListItem>
              );
            }
            return (
              <ListItem component="div">
                <ListItemText classes={{ primary: s.primaryText }}>
                  {getCategoryLabel(selectedCategory)}
                </ListItemText>
              </ListItem>
            );
          }}
        >
          {Object.values(ProgramTemplateCategory).map((cat) => (
            <MenuItem key={cat} value={cat}>
              {getCategoryLabel(cat)}
            </MenuItem>
          ))}
        </Select>
      </FieldsGroup>

      <LoadingButton
        className={s.button}
        fullWidth
        variant="contained"
        onClick={handleConfirm}
        disabled={!name || disabled || !!error}
        loading={isPending}
      >
        Create template
      </LoadingButton>
    </BaseDialog>
  );
}
